import Button from 'components/Button';
import DataTable from 'components/DataTable';
import Input from 'components/Input';
import ListActionsWrapper from 'components/ListActionsWrapper';
import ListWrapper from 'components/ListWrapper';
import Modal from 'components/Modal';
import Pagination from 'components/Pagination';
import ProductImage from 'components/ProductImage';
import SearchInput from 'components/SearchInput';
import Typography from 'components/Typography';
import {ProductTypeFilter} from 'hooks/useProductList';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {DataItem, DataTableSchemaItem, Product} from 'types';
import styles from './Product.module.scss';
import api from 'api';
import toast from 'react-hot-toast';
import {get} from 'lodash';
import Select from 'components/Select';
import FormActionGroup from 'components/FormActionGroup';
import {Controller, useForm} from 'react-hook-form';
import ListTabs from 'pages/UserForm/ListTabs';
import DeleteModal from 'components/DeleteModal';
import useAuth from 'hooks/useAuth';

const SCHEMA: DataTableSchemaItem[] = [
  {dataKey: 'id', header: 'Product ID', colWidth: '120px', sortable: true},
  {dataKey: 'image', header: 'Image', colWidth: '200px'},
  {dataKey: 'name', header: 'Name', colWidth: '2fr', sortable: true},
  // {dataKey: 'strength', header: 'Strength'},
  // {dataKey: 'description', header: 'Description'},
  {dataKey: 'price', header: 'Price', sortable: true},
  {
    dataKey: 'total_prescriptions',
    header: '# of Times Ordered',
    sortable: true,
    align: 'center',
  },
];

const tabs = [
  {title: 'All', value: ''},
  {title: 'OTC', value: 'otc'},
  {title: 'Prescription', value: 'prescription'},
  {title: 'Featured', value: 'featured'},
];

type Props = {
  isLoading: boolean;
  products: Product[];
  totalPages: number;
  currentPage?: number;
  search?: string;
  refetch: () => void;
  onProductTypeChange: (value: ProductTypeFilter) => void;
  productType?: ProductTypeFilter;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  sortBy: string;
  sortOrder: 'desc' | 'asc';
  count?: number;
  onSort: (sort: string, orderBy: 'desc' | 'asc') => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  setCurrentPage: (page: number) => void;
  totalPrescriptions?: number;
};

const Products: React.VFC<Props> = ({
  products,
  productType = '',
  onProductTypeChange,
  totalPages,
  search = '',
  refetch,
  handleSearchChange,
  currentPage = 1,
  sortBy,
  sortOrder,
  onSort,
  count,
  pageSize,
  setPageSize,
  isLoading,
  setCurrentPage,
  totalPrescriptions,
}) => {
  const {rolePermission} = useAuth();
  const [visible, setVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product>();

  const handleSelectProduct = useCallback(
    (dataItem: DataItem) => {
      const product = products.find((item) => item.id === dataItem.id);
      setSelectedProduct(product);
    },
    [products]
  );

  const data = useMemo(() => {
    return products.map((product) => ({
      ...product,
      id: product.id,
      image: <ProductImage product={product} />,
      name: product.name,
      strength: product.strength?.replaceAll('/', ' / '),
      description: product.description,
      price: product.price ? '$' + product.price : '',
    }));
  }, [products]);

  const onChangeTabs = useCallback(
    (nextValue: string) => {
      setCurrentPage(1);
      onProductTypeChange(nextValue as ProductTypeFilter);
    },
    [onProductTypeChange, setCurrentPage]
  );

  const deleteProduct = async () => {
    try {
      await api.delete(`/products/${selectedProduct?.id}`);
      refetch();
      setSelectedProduct(undefined);
    } catch (error) {
      toast.error(get(error, 'message', ''));
    }
  };

  const canWrite = rolePermission['products']?.permissions?.write;

  return (
    <>
      <div style={{display: 'flex', justifyContent: 'space-between'}}>
        <Typography variant="h2">All products</Typography>
        <div className={styles.header}>
          {!!canWrite && (
            <Button onClick={() => setVisible(true)} color="blue">
              Add Product
            </Button>
          )}
          <Typography textAlign="right" variant="h4">
            Total Products: {Number(count).toLocaleString('en-US') || 0}
          </Typography>
          <Typography textAlign="right" variant="h4">
            Total Orders:{' '}
            {Number(totalPrescriptions).toLocaleString('en-US') || 0}
          </Typography>
        </div>
      </div>
      <ListWrapper>
        <ListTabs
          tab="products_tab"
          tabs={tabs}
          activeTabValue={productType}
          onChange={onChangeTabs}
        />
        <ListActionsWrapper>
          <SearchInput
            placeholder="Search"
            onChange={handleSearchChange}
            value={search}
          />
        </ListActionsWrapper>
        <DataTable
          data={data}
          loading={isLoading}
          schema={SCHEMA}
          selectable={false}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          onClick={handleSelectProduct}
        />
        <Pagination
          count={count || 0}
          page={currentPage}
          total={totalPages}
          pageSize={pageSize}
          setCurrentPage={setCurrentPage}
          setPageSize={setPageSize}
        />
      </ListWrapper>
      <NewProduct
        onDelete={deleteProduct}
        visible={visible || !!selectedProduct}
        product={selectedProduct}
        onClose={() => {
          setSelectedProduct(undefined);
          setVisible(false);
        }}
        setSelectedProduct={setSelectedProduct}
        onSuccess={() => {
          refetch();
        }}
      />
    </>
  );
};

type ProductProps = {
  visible: boolean;
  onClose: () => void;
  onSuccess: (data: Product) => void;
  product?: Product;
  onDelete?: () => void;
  setSelectedProduct?: (data: Product) => void;
};

export const NewProduct = ({
  visible,
  onClose,
  onSuccess,
  product,
  onDelete,
  setSelectedProduct,
}: ProductProps) => {
  const {rolePermission} = useAuth();
  const canWrite = rolePermission['products']?.permissions?.write;
  const canDelete = rolePermission['products']?.permissions?.delete;
  const {control, handleSubmit, reset, setValue} = useForm();
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState<any>(null);
  const [deleteModal, setDeleteModal] = useState(false);

  useEffect(() => {
    if (product?.id) {
      setValue('name', product.name);
      setValue('price', product.price);
      setValue('strength', product.strength);
      setDescription(product.description);
      setValue(
        'is_prescription',
        product.is_prescription !== null
          ? {
              value: product?.is_prescription ? 'true' : 'false',
              label: product?.is_prescription ? 'Prescription' : 'OTC',
            }
          : undefined
      );
    } else {
      setDescription('');
      reset();
      setImage(null);
    }
  }, [product, reset, setValue]);

  const onSubmit = (data: any) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append(
      'is_prescription',
      data?.is_prescription !== null ? data?.is_prescription?.value : ''
    );
    formData.append('price', data.price.replace(/\s/g, ''));
    formData.append('description', description);
    formData.append('strength', data.strength);

    if (product?.image === undefined) {
      //@ts-ignore
      formData.append('image.file', null);
    }

    if (image) {
      formData.append('image.file', image);
    }

    const request = product?.id
      ? api.put(`/products/${product.id}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      : api.post('/products/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

    request
      .then((res) => {
        onSuccess(res.data);
        setDescription('');
        setImage(null);
        reset();
        onClose();
      })
      .catch((error) => {
        const errors = get(error, 'response,data', {});
        const error_arr = Object.values(errors);
        toast.error(
          (get(error_arr, '[0]', '') as string) || 'Something went wrong'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      isOpen={visible}
      onClose={onClose}
      className={styles.list_wrapper}
      onRequestClose={onClose}
    >
      <Typography variant="h2">
        {product ? product.name : 'Add new product'}
      </Typography>
      <div className={styles.label}>Image: </div>
      <ProductImage
        showDelete={true}
        size="medium"
        product={product}
        onChange={(value) => {
          if (setSelectedProduct && product?.id) {
            //@ts-ignore
            setSelectedProduct({...product, image: value});
          }
          setImage(value);
        }}
      />
      <div className={styles.label}>Name:</div>
      <Controller
        name="name"
        control={control}
        rules={{required: true}}
        render={({field, fieldState}) => (
          <Input
            {...field}
            placeholder="Name"
            hasError={!!fieldState.error}
            className={styles.root}
          />
        )}
      />
      <div className={styles.label}>Strength</div>
      <Controller
        name="strength"
        control={control}
        rules={{required: true}}
        render={({field, fieldState}) => (
          <Input
            {...field}
            placeholder="strength"
            hasError={!!fieldState.error}
            className={styles.root}
          />
        )}
      />
      <div className={styles.label}>Description:</div>
      <textarea
        value={description}
        onChange={(event) => {
          setDescription(event.target.value);
        }}
        placeholder="Description"
        className={styles.textarea}
      />
      <div className={styles.label}>Price:</div>
      <Controller
        name="price"
        control={control}
        rules={{
          required: {
            value: true,
            message: 'Required',
          },
        }}
        render={({field, fieldState}) => (
          <Input
            {...field}
            hasError={!!fieldState.error?.message}
            onChange={(value) => {
              let val = value.target.value;
              let dec = '';
              if (val.split('.').length > 1) {
                dec = '.' + val.split('.')[1].replace(/\D/g, '');
                val = val.split('.')[0];
              }
              field.onChange(
                val.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ') +
                  `${dec}`
              );
            }}
            placeholder="Price"
            className={styles.root}
          />
        )}
      />
      {!product && (
        <>
          <div className={styles.label}>Click to select</div>
          <Controller
            name="is_prescription"
            control={control}
            render={({field, fieldState}) => (
              <Select
                {...field}
                error={fieldState.error?.message}
                options={[
                  {
                    label: 'Prescription',
                    value: 'true',
                  },
                  {
                    label: 'OTC',
                    value: 'false',
                  },
                ]}
              />
            )}
          />
        </>
      )}

      <FormActionGroup className={styles.buttonWrap}>
        {product && !!canDelete ? (
          <Button color="danger" size="md" onClick={() => setDeleteModal(true)}>
            Delete
          </Button>
        ) : (
          <></>
        )}
        {!!canWrite && (
          <Button
            loading={loading}
            onClick={handleSubmit(onSubmit)}
            color="green"
          >
            {product ? 'Update product' : 'Add Product'}
          </Button>
        )}
      </FormActionGroup>
      {!!product && (
        <DeleteModal
          openModal={deleteModal}
          setOpenModal={setDeleteModal}
          onDelete={() => onDelete?.()}
          text={`Are you sure, you want to delete this product?`}
        />
      )}
    </Modal>
  );
};

export default Products;
